'use client';

// Header.tsx

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { WHATSAPP_URL } from '@/components/WhatsAppFloat';

const NAV_LINKS = [
  { href: '/', label: 'Inicio' },
  { href: '/servicios', label: 'Servicios' },
  { href: '/nosotros', label: 'Nosotros' },
  { href: '/contacto', label: 'Contacto' },
];

// Matches the original markup: only index.html and certificates.html carried
// the `reveal-header` class (delayed fade-in after the page loader).
export default function Header() {
  const pathname = usePathname();
  const revealHeader = pathname === '/' || pathname.startsWith('/certificates');
  const [solid, setSolid] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [activeHeader, setActiveHeader] = useState(!revealHeader);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastScroll = useRef(0);

  // Re-runs on every pathname change (not just mount) so navigating back to
  // a reveal-header page replays the fade-in, matching the original's
  // full-page-reload behavior even though the Header itself never unmounts
  // across client-side App Router navigations.
  useEffect(() => {
    // Header persists across client-side navigations (it isn't remounted per
    // route), so replaying the timed reveal on each pathname change needs an
    // explicit reset here rather than fresh initial state.
    if (!revealHeader) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveHeader(true);
      return;
    }
    setActiveHeader(false);
    const timer = setTimeout(() => setActiveHeader(true), 2000);
    return () => clearTimeout(timer);
  }, [revealHeader, pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      setSolid(currentScroll > 80);
      setHidden(currentScroll > lastScroll.current && currentScroll > 300);
      lastScroll.current = currentScroll;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [menuOpen]);

  const headerClassName = [
    solid && 'solid',
    hidden && 'hide',
    revealHeader && 'reveal-header',
    activeHeader && 'active-header',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header id="main-header" className={headerClassName}>
      <Link href="/" className="logo-link">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/images/logos/logo.webp" alt="VANGUAR" className="header-logo-img" />
      </Link>

      <div className="header-actions">
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mobile-wpp" title="Escribinos por WhatsApp">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/logo-wpp.webp" alt="WhatsApp" />
        </a>
        <button
          className={`menu-toggle${menuOpen ? ' active' : ''}`}
          id="menu-toggle"
          aria-label="Abrir menú"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <nav id="main-nav" className={menuOpen ? 'active' : ''}>
        <ul>
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <Link href={link.href} onClick={() => setMenuOpen(false)}>
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
}
